// src/.../businessAddressForm.tsx
import React from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { City, Country, State } from "../../../types/countryStateCity";

export interface BusinessAddress {
  street?: string;
  cityId?: number;
  stateId?: number;
  countryId?: number;
  zipcode?: string;
}

interface BusinessAddressFormProps {
  disabled: boolean;
  businessAddress: BusinessAddress | undefined;
  countries: Country[];
  setBusinessAddress: React.Dispatch<
    React.SetStateAction<BusinessAddress | undefined>
  >;
  setActiveCountry: (country: Country) => void;
}

/**
 * BusinessAddressForm (TSX)
 * - All Select values are strings ("" | "1" | "2" | ...)
 * - Convert to number only when persisting to businessAddress
 * - Includes placeholder "" menu items to avoid MUI "out-of-range" warnings
 * - Shows simple "touched" validation messaging
 */
const BusinessAddressForm: React.FC<BusinessAddressFormProps> = ({
  disabled,
  businessAddress,
  countries,
  setBusinessAddress,
  setActiveCountry,
}) => {
  // Equality helper (handles string/number ids)
  const eqId = (a: unknown, b: unknown) => Number(a) === Number(b);

  // Derive selected entities from current address ids
  const selectedCountry: Country | null =
    businessAddress?.countryId != null
      ? countries.find((c) => eqId(c.id, businessAddress.countryId)) ?? null
      : null;

  const selectedState: State | null =
    selectedCountry?.states?.find((s) => eqId(s.id, businessAddress?.stateId)) ??
    null;

  const selectedCity: City | null =
    selectedState?.cities?.find((c) => eqId(c.id, businessAddress?.cityId)) ??
    null;

  const hasStates = Boolean(selectedCountry?.states?.length);
  const hasCities = Boolean(selectedState?.cities?.length);

  // Ensure Select values are always strings (MUI expects exact primitive equality)
  const asSelectValue = (id?: number) => (id != null ? String(id) : "");
  const countryValue = asSelectValue(businessAddress?.countryId);
  const stateValue = asSelectValue(businessAddress?.stateId);
  const cityValue = asSelectValue(businessAddress?.cityId);

  // Track touched fields for helper text
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const markTouched = (key: string) =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  // Handlers
  const handleCountryChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value; // "" or "123"
    const nextCountry =
      countries.find((c) => String(c.id) === v) ?? null;

    setBusinessAddress((prev) => ({
      ...(prev ?? {}),
      countryId: v ? Number(v) : undefined,
      stateId: undefined,
      cityId: undefined,
    }));

    if (nextCountry) {
      setActiveCountry(nextCountry);
    }
  };

  const handleStateChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value;
    setBusinessAddress((prev) => ({
      ...(prev ?? {}),
      stateId: v ? Number(v) : undefined,
      cityId: undefined,
    }));
  };

  const handleCityChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value;
    setBusinessAddress((prev) => ({
      ...(prev ?? {}),
      cityId: v ? Number(v) : undefined,
    }));
  };

  const handleFieldChange = (
    field: keyof BusinessAddress,
    value: string
  ) => {
    setBusinessAddress((prev) => ({
      ...(prev ?? {}),
      [field]: value,
    }));
  };

  return (
    <Grid container spacing={4}>
      {/* Country */}
      <Grid item xs={12} sm={4}>
        <FormControl
          variant="outlined"
          fullWidth
          disabled={disabled}
          error={touched.country && !businessAddress?.countryId}
        >
          <InputLabel id="country-label">Country</InputLabel>
          <Select
            id="country"
            labelId="country-label"
            label="Country"
            required
            value={countryValue}
            onChange={handleCountryChange}
            onBlur={() => markTouched("country")}
          >
            {/* Placeholder keeps the value in-range when unset */}
            <MenuItem value="">
              <em>Select a country</em>
            </MenuItem>

            {countries.map((country) => (
              <MenuItem key={String(country.id)} value={String(country.id)}>
                {country.name}
              </MenuItem>
            ))}
          </Select>
          {touched.country && !businessAddress?.countryId && (
            <FormHelperText>Country is required</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* State */}
      <Grid item xs={12} sm={4}>
        <FormControl
          variant="outlined"
          fullWidth
          disabled={disabled || !hasStates}
          error={touched.state && !businessAddress?.stateId}
        >
          <InputLabel id="state-label">State</InputLabel>
          <Select
            id="state"
            labelId="state-label"
            label="State"
            required
            value={stateValue}
            onChange={handleStateChange}
            onBlur={() => markTouched("state")}
          >
            <MenuItem value="">
              <em>{hasStates ? "Select a state" : "—"}</em>
            </MenuItem>

            {selectedCountry?.states?.map((state) => (
              <MenuItem key={String(state.id)} value={String(state.id)}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
          {touched.state && !businessAddress?.stateId && hasStates && (
            <FormHelperText>State is required</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* City */}
      <Grid item xs={12} sm={4}>
        <FormControl
          variant="outlined"
          fullWidth
          disabled={disabled || !hasCities}
          error={touched.city && !businessAddress?.cityId}
        >
          <InputLabel id="city-label">City</InputLabel>
          <Select
            id="city"
            labelId="city-label"
            label="City"
            required
            value={cityValue}
            onChange={handleCityChange}
            onBlur={() => markTouched("city")}
          >
            <MenuItem value="">
              <em>{hasCities ? "Select a city" : "—"}</em>
            </MenuItem>

            {selectedState?.cities?.map((city) => (
              <MenuItem key={String(city.id)} value={String(city.id)}>
                {city.name}
              </MenuItem>
            ))}
          </Select>
          {touched.city && !businessAddress?.cityId && hasCities && (
            <FormHelperText>City is required</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Street */}
      <Grid item xs={12}>
        <TextField
          autoComplete="off"
          disabled={disabled}
          required
          label="Street Address"
          id="street"
          name="street"
          variant="outlined"
          fullWidth
          value={businessAddress?.street ?? ""}
          onChange={(e) => handleFieldChange("street", e.target.value)}
          onBlur={() => markTouched("street")}
          error={touched.street && !businessAddress?.street}
          helperText={
            touched.street && !businessAddress?.street
              ? "Street address is required"
              : ""
          }
        />
      </Grid>

      {/* Zip Code */}
      <Grid item xs={12} sm={4} marginBottom="20px">
        <TextField
          disabled={disabled}
          required
          label="Zip Code"
          id="zipcode"
          name="zipcode"
          variant="outlined"
          fullWidth
          value={businessAddress?.zipcode ?? ""}
          onChange={(e) => handleFieldChange("zipcode", e.target.value)}
          onBlur={() => markTouched("zipcode")}
          error={touched.zipcode && !businessAddress?.zipcode}
          helperText={
            touched.zipcode && !businessAddress?.zipcode
              ? "Zip code is required"
              : ""
          }
        />
      </Grid>
    </Grid>
  );
};

export default BusinessAddressForm;
